// app/src/components/planning/plannerDecisionPanel.tsx

import { useState, useRef, useEffect } from 'react';

// ── Config ────────────────────────────────────────────────────────────────────

const MARGIN_THRESHOLD = 4.0; // single source of truth — used in copy, mock gen, and trigger derivation

// ── Types ─────────────────────────────────────────────────────────────────────

interface RecommendationSnapshot {
  margin_pct: number;
  margin_threshold_pct: number;
  revenue_gbp: number;
  job_count: number;
}

interface SourceRef {
  job_id: string;
  order_id: string;
}

interface RecommendationContext {
  job_id?: string;
  order_id?: string;
  vehicle_entity_id?: string;
  vehicle_registration?: string;
}

interface DrawerProps {
  content: React.ReactNode;
}

const MIN_WIDTH = 320;
const MAX_WIDTH_RATIO = 0.85;
const DEFAULT_WIDTH = 480;

type TriggerType = 'EXCEPTION' | 'ROUTINE';

export interface Recommendation {
  recommendation_id: string;
  recommendation_type: string;
  recommendation_text: string;
  route_id: string;
  delivery_date: string;
  trigger_type?: TriggerType; // optional on the wire; derived as fallback if absent
  recommendation_snapshot?: RecommendationSnapshot;
  recommendation_context?: RecommendationContext;
  source_ref?: SourceRef;
  confidence_level?: 'Confirmed' | 'Estimated' | 'Projected';
}

type Action = 'ACCEPT' | 'MODIFY' | 'REJECT';

export interface DecisionPayload {
  recommendation_id: string;
  recommendation_type: string;
  recommendation_text: string;
  route_id: string;
  delivery_date: string;
  trigger_type: TriggerType;
  recommendation_snapshot?: RecommendationSnapshot;
  recommendation_context?: RecommendationContext;
  source_ref?: SourceRef;
  confidence_level?: Recommendation['confidence_level'];
  planner_action: Action;
  modification_detail?: { category: string; notes: string };
}

// ── Trigger helpers ───────────────────────────────────────────────────────────

// Derive EXCEPTION/ROUTINE from margin vs threshold when the rec doesn't carry it explicitly.
function deriveTrigger(rec: Recommendation): TriggerType {
  if (rec.trigger_type) return rec.trigger_type;
  const snap = rec.recommendation_snapshot;
  if (!snap) return 'EXCEPTION';
  return snap.margin_pct < snap.margin_threshold_pct ? 'EXCEPTION' : 'ROUTINE';
}

function isException(rec: Recommendation): boolean {
  return deriveTrigger(rec) === 'EXCEPTION';
}

// Default log entry for a routine job the planner never touched:
// ROUTINE + ACCEPT = "saw it, didn't object" (implicit/weak signal).
function makeRoutineDefault(rec: Recommendation): DecisionPayload {
  return {
    recommendation_id: rec.recommendation_id,
    recommendation_type: rec.recommendation_type,
    recommendation_text: rec.recommendation_text,
    route_id: rec.route_id,
    delivery_date: rec.delivery_date,
    trigger_type: 'ROUTINE',
    recommendation_snapshot: rec.recommendation_snapshot,
    recommendation_context: rec.recommendation_context,
    source_ref: rec.source_ref,
    confidence_level: rec.confidence_level,
    planner_action: 'ACCEPT',
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const today = new Date();
const day = (offset: number) => {
  const d = new Date(today);
  d.setDate(d.getDate() + offset);
  return d.toISOString().split('T')[0];
};

function formatDayHeading(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  const tomorrowStr = day(1);
  if (dateStr === tomorrowStr) {
    return 'Tomorrow — ' + date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  }
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

const COLUMN_WIDTH = 520; // px — doubled to fit info OR action content per card

// ── Grouping helper ────────────────────────────────────────────────────────

function groupByJob(recs: Recommendation[]): Recommendation[][] {
  const map = new Map<string, Recommendation[]>();
  for (const rec of recs) {
    const key = rec.source_ref?.job_id ?? rec.recommendation_id; // fallback: ungrouped
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(rec);
  }
  return Array.from(map.values());
}

// ── Mock data (temporary — move to API once wired up) ─────────────────────────

const VEHICLES = [
  'VH001', 'VH002', 'VH003', 'VH004', 'VH005',
  'VH006', 'VH007', 'VH008', 'VH009', 'VH010',
];

const CONFIDENCE: Recommendation['confidence_level'][] = [
  'Confirmed',
  'Estimated',
  'Projected',
];

// Per day: [exceptions below threshold, routine jobs above threshold]
const EXCEPTION_COUNTS = [8, 7, 6, 5, 4];
const ROUTINE_COUNTS = [22, 20, 18, 14, 12];

const MOCK_RECOMMENDATIONS: Recommendation[] = [];

let id = 1;

for (let d = 0; d < EXCEPTION_COUNTS.length; d++) {
  // job pools + route map reset per day so groups never span dates
  const jobPoolForDay: string[] = [];
  const routeForJob = new Map<string, string>();

  const pushRec = (trigger: TriggerType) => {
    // exceptions land below threshold, routine above
    const margin =
      trigger === 'EXCEPTION'
        ? +(Math.random() * (MARGIN_THRESHOLD - 0.1)).toFixed(1)
        : +(MARGIN_THRESHOLD + Math.random() * 10).toFixed(1);

    // 40% chance to reuse an existing job_id from today's pool (once there is one)
    const reuseExisting = jobPoolForDay.length > 0 && Math.random() < 0.4;
    const jobId = reuseExisting
      ? jobPoolForDay[Math.floor(Math.random() * jobPoolForDay.length)]
      : `job_${String(id).padStart(4, '0')}`;

    if (!reuseExisting) jobPoolForDay.push(jobId);

    let routeId = routeForJob.get(jobId);
    if (!routeId) {
      routeId = `JOB-${day(d)}-HD-${
        VEHICLES[Math.floor(Math.random() * VEHICLES.length)]
      }-T${1 + Math.floor(Math.random() * 3)}`;
      routeForJob.set(jobId, routeId);
    }

    MOCK_RECOMMENDATIONS.push({
      recommendation_id: `rec_${String(id).padStart(3, '0')}`,
      recommendation_type: trigger === 'EXCEPTION' ? 'margin_below_threshold' : 'routine_dispatch',
      recommendation_text:
        trigger === 'EXCEPTION' ? 'Margin below threshold.' : 'Routine dispatch — margin above threshold.',

      route_id: routeId,
      delivery_date: day(d),
      trigger_type: trigger,

      recommendation_snapshot: {
        margin_pct: margin,
        margin_threshold_pct: MARGIN_THRESHOLD,
        revenue_gbp: 500 + Math.floor(Math.random() * 5000),
        job_count: 1 + Math.floor(Math.random() * 5),
      },

      source_ref: {
        job_id: jobId,
        order_id: `ORD-${4500 + id}`,
      },

      confidence_level: CONFIDENCE[Math.floor(Math.random() * CONFIDENCE.length)],
    });

    id++;
  };

  for (let i = 0; i < EXCEPTION_COUNTS[d]; i++) pushRec('EXCEPTION');
  for (let i = 0; i < ROUTINE_COUNTS[d]; i++) pushRec('ROUTINE');
}

const CONFIDENCE_LABELS: Record<string, string> = {
  Confirmed: 'Confirmed data',
  Estimated: 'Estimated data',
  Projected: 'Projected data',
};

const MODIFY_REASONS = [
  'Last Minute', 'Vehicle Change', 'Date Change', 'Route Change',
  'Customer Request', 'Load Consolidation', 'Operational Constraint', 'Other',
];

const REJECT_REASONS = [
  'Last Minute', 'Customer Commitment', 'Operational Requirement',
  'Recommendation Incorrect', 'Already Reviewed', 'Strategic Account', 'False Positive', 'Other',
];

function groupByDate(recs: Recommendation[]): [string, Recommendation[]][] {
  const map = new Map<string, Recommendation[]>();
  for (const rec of recs) {
    if (!map.has(rec.delivery_date)) map.set(rec.delivery_date, []);
    map.get(rec.delivery_date)!.push(rec);
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
}

// ── View mode toggle (global — drives every card in every column) ────────────

type ViewMode = 'info' | 'decide';

function ViewModeToggle({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-card p-0.5">
      {(['info', 'decide'] as ViewMode[]).map((m) => (
        <button
          key={m}
          onClick={() => onChange(m)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            mode === m ? 'bg-accent text-white' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {m === 'info' ? 'Info' : 'Decide'}
        </button>
      ))}
    </div>
  );
}

// ── Compact job card — renders info OR decide content depending on mode ──────

function JobCard({
  recommendation,
  mode,
  onDecision,
  initialDecision,
  hideRouteHeader = false,
}: {
  recommendation: Recommendation;
  mode: ViewMode;
  onDecision: (payload: DecisionPayload) => void;
  initialDecision?: DecisionPayload | null;
  hideRouteHeader?: boolean;
}) {
  const [selected, setSelected] = useState<Action | null>(initialDecision?.planner_action ?? null);
  const [reason, setReason] = useState(initialDecision?.modification_detail?.notes ?? '');
  const [reasonCategory, setReasonCategory] = useState(initialDecision?.modification_detail?.category ?? '');
  const [confirmed, setConfirmed] = useState(!!initialDecision);
  const [error, setError] = useState<string | null>(null);

  const needsReason = selected === 'MODIFY' || selected === 'REJECT';
  const snap = recommendation.recommendation_snapshot;

  function handleConfirm() {
    if (!selected) return;
    if (needsReason && !reasonCategory) {
      setError('Please select a reason category.');
      return;
    }
    setError(null);
    setConfirmed(true);

    const payload: DecisionPayload = {
      recommendation_id: recommendation.recommendation_id,
      recommendation_type: recommendation.recommendation_type,
      recommendation_text: recommendation.recommendation_text,
      route_id: recommendation.route_id,
      delivery_date: recommendation.delivery_date,
      trigger_type: deriveTrigger(recommendation),
      recommendation_snapshot: recommendation.recommendation_snapshot,
      recommendation_context: recommendation.recommendation_context,
      source_ref: recommendation.source_ref,
      confidence_level: recommendation.confidence_level,
      planner_action: selected,
      modification_detail: needsReason ? { category: reasonCategory, notes: reason.trim() } : undefined,
    };

    onDecision(payload);
  }

  const statusColor =
    selected === 'ACCEPT'
      ? 'bg-success/40 border-success text-success'
      : selected === 'MODIFY'
      ? 'bg-ring/40 border-ring text-ring'
      : 'bg-destructive/40 border-destructive text-destructive';

  return (
    <div className="border border-border rounded-xl bg-card flex flex-col">
      {/* Route header — full version for standalone cards */}
      {!hideRouteHeader && (
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/70">
          <p className="text-xs font-medium text-white truncate" title={recommendation.route_id}>
            {recommendation.route_id}
          </p>
          {confirmed && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap ${statusColor}`}>
              {selected === 'ACCEPT' ? 'Accepted' : selected === 'MODIFY' ? 'Modified' : 'Rejected'}
            </span>
          )}
        </div>
      )}

      {/* Mini context line — for grouped rows, so a row is never just bare buttons.
          Shown instead of the full header when hideRouteHeader is true. */}
      {hideRouteHeader && (
        <div className="flex items-center justify-between px-3 py-1 border-b border-border/50 bg-card/40">
          <p className="text-xs text-muted-foreground">
            {recommendation.source_ref?.order_id ?? 'Unknown order'}
          </p>
          {confirmed && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap ${statusColor}`}>
              {selected === 'ACCEPT' ? 'Accepted' : selected === 'MODIFY' ? 'Modified' : 'Rejected'}
            </span>
          )}
        </div>
      )}

      {mode === 'info' ? (
        // ── INFO VIEW ──────────────────────────────────────────────────────
        <div className="px-3 py-2 grid grid-cols-2 gap-y-2 gap-x-3">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Order</p>
            <p className="text-xs text-white">{recommendation.source_ref?.order_id ?? 'Unknown'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Margin</p>
            <p className="text-xs text-white">{snap?.margin_pct ?? '-'}%</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Revenue</p>
            <p className="text-xs text-white">£{snap?.revenue_gbp ?? '-'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Estimation</p>
            {recommendation.confidence_level && (
              <div className="col-span-2">
                <span className="text-xs px-2 py-0.5 rounded-md bg-ring/30 text-ring border border-ring/40 whitespace-nowrap">
                  {CONFIDENCE_LABELS[recommendation.confidence_level]}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        // ── DECIDE VIEW ────────────────────────────────────────────────────
        <div className="px-3 py-2">
          {confirmed ? (
            <button
              onClick={() => { setConfirmed(false); setError(null); }}
              className="w-full h-8 text-xs rounded-md border border-accent bg-secondary text-foreground hover:bg-accent"
            >
              Amend
            </button>
          ) : (
            <>
              <div className="flex gap-1.5 mb-2">
                {(['ACCEPT', 'MODIFY', 'REJECT'] as Action[]).map((action) => (
                  <button
                    key={action}
                    onClick={() => { setSelected(action); setError(null); setReason(''); setReasonCategory(''); }}
                    className={`flex-1 h-8 text-xs font-medium rounded-md border transition-colors ${
                      selected === action
                        ? action === 'ACCEPT'
                          ? 'bg-success border-success text-white'
                          : action === 'MODIFY'
                          ? 'bg-ring border-ring text-white'
                          : 'bg-destructive/60 border-destructive text-destructive'
                        : action === 'ACCEPT'
                        ? 'bg-success/40 border-success/60 text-success hover:bg-success/50'
                        : action === 'MODIFY'
                        ? 'bg-ring/40 border-ring/60 text-ring hover:bg-ring/50'
                        : 'bg-destructive/40 border-destructive/60 text-destructive hover:bg-destructive/50'
                    }`}
                  >
                    {action}
                  </button>
                ))}
              </div>

              {selected && needsReason && (
                <div className="mb-2 space-y-2">
                  <select
                    value={reasonCategory}
                    onChange={(e) => setReasonCategory(e.target.value)}
                    className="w-full text-xs border border-accent rounded-md px-2 py-1.5 bg-secondary text-white focus:outline-none focus:border-muted-foreground"
                  >
                    <option value="">Select a reason...</option>
                    {(selected === 'MODIFY' ? MODIFY_REASONS : REJECT_REASONS).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <textarea
                    rows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Additional context..."
                    className="w-full text-xs border border-accent rounded-md px-2 py-1.5 resize-none bg-secondary text-white focus:outline-none focus:border-muted-foreground"
                  />
                </div>
              )}

              {error && <p className="text-xs text-destructive mb-2">{error}</p>}

              <button
                onClick={handleConfirm}
                disabled={!selected}
                className="w-full h-8 rounded-md border border-accent bg-secondary text-xs font-medium text-white hover:bg-accent disabled:opacity-40"
              >
                Confirm
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Job group wrapper — merges same-job orders into one visual block ────────

function JobGroup({
  group,
  mode,
  onDecision,
  decisions,
  defaultDecisionFor,
}: {
  group: Recommendation[];
  mode: ViewMode;
  onDecision: (payload: DecisionPayload) => void;
  decisions: Record<string, DecisionPayload>;
  defaultDecisionFor?: (rec: Recommendation) => DecisionPayload | null;
}) {
  const decisionFor = (rec: Recommendation): DecisionPayload | null =>
    decisions[rec.recommendation_id] ?? defaultDecisionFor?.(rec) ?? null;

  if (group.length === 1) {
    return (
      <JobCard
        recommendation={group[0]}
        mode={mode}
        onDecision={onDecision}
        initialDecision={decisionFor(group[0])}
      />
    );
  }

  const first = group[0];
  const totalRevenue = group.reduce(
    (sum, rec) => sum + (rec.recommendation_snapshot?.revenue_gbp ?? 0),
    0,
  );

  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden shrink-0">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/70 bg-card/80">
        <p className="text-xs font-medium text-white truncate" title={first.route_id}>
          {first.route_id}
        </p>
        <div className="flex items-center gap-2 whitespace-nowrap">
          <span className="text-xs font-medium text-foreground">
            Revenue: £{totalRevenue.toLocaleString('en-GB')}
          </span>
          <span className="text-xs text-muted-foreground px-1.5 py-0.5 rounded-full bg-secondary/60 border border-accent">
            {group.length} orders
          </span>
        </div>
      </div>

      <div className="flex flex-col divide-y divide-border/70">
        {group.map((rec) => (
          <JobCard
            key={rec.recommendation_id}
            recommendation={rec}
            mode={mode}
            onDecision={onDecision}
            initialDecision={decisionFor(rec)}
            hideRouteHeader
          />
        ))}
      </div>
    </div>
  );
}

// ── Routine section — collapsed, auto-accepted, amendable ────────────────────

function RoutineSection({
  routineRecs,
  mode,
  onDecision,
  decisions,
}: {
  routineRecs: Recommendation[];
  mode: ViewMode;
  onDecision: (payload: DecisionPayload) => void;
  decisions: Record<string, DecisionPayload>;
}) {
  const [expanded, setExpanded] = useState(false);

  if (routineRecs.length === 0) return null;

  // how many routine jobs has the planner actively touched (amended away from the default)?
  const amendedCount = routineRecs.filter(
    (r) => decisions[r.recommendation_id] && decisions[r.recommendation_id].planner_action !== 'ACCEPT',
  ).length;

  return (
    <div className="rounded-xl border border-border/60 bg-card/30">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
      >
        <div className="flex items-center gap-2">
          <svg
            width="12" height="12" viewBox="0 0 14 14" fill="none"
            className={`text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`}
          >
            <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="text-xs font-medium text-foreground">
            {routineRecs.length} routine {routineRecs.length === 1 ? 'job' : 'jobs'} · auto-accepted
          </span>
        </div>
        {amendedCount > 0 && (
          <span className="text-xs px-1.5 py-0.5 rounded-full border border-warning/50 bg-warning/30 text-warning whitespace-nowrap">
            {amendedCount} amended
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-2 pb-2 flex flex-col gap-2 opacity-90">
          {groupByJob(routineRecs).map((group) => (
            <JobGroup
              key={group[0].recommendation_id}
              group={group}
              mode={mode}
              onDecision={onDecision}
              decisions={decisions}
              defaultDecisionFor={makeRoutineDefault}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Column ─────────────────────────────────────────────────────────────────

function DayColumn({
  dateStr,
  recs,
  mode,
  decisions,
  onDecision,
}: {
  dateStr: string;
  recs: Recommendation[];
  mode: ViewMode;
  decisions: Record<string, DecisionPayload>;
  onDecision: (payload: DecisionPayload) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exceptionRecs = recs.filter(isException);
  const routineRecs = recs.filter((r) => !isException(r));

  // Gate only on exceptions — routine jobs are always ready (auto-accepted).
  const decidedInDay = exceptionRecs.filter((r) => decisions[r.recommendation_id]).length;
  const allDayDecided = decidedInDay === exceptionRecs.length;

  async function handleSubmitDay() {
    // exceptions: whatever the planner decided
    const exceptionPayloads = exceptionRecs
      .map((r) => decisions[r.recommendation_id])
      .filter(Boolean) as DecisionPayload[];

    // routine: planner's amendment if they touched it, otherwise the ROUTINE/ACCEPT default
    const routinePayloads = routineRecs.map(
      (r) => decisions[r.recommendation_id] ?? makeRoutineDefault(r),
    );

    const payloads = [...exceptionPayloads, ...routinePayloads];

    setSubmitting(true);
    setError(null);

    try {
      const results = await Promise.allSettled(
        payloads.map((payload) =>
          fetch('/api/planning/decisions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }).then(async (res) => {
            if (!res.ok) {
              const data = await res.json();
              throw new Error(data.error || `Failed for ${payload.recommendation_id}`);
            }
            return res.json();
          })
        )
      );

      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        setError(`${failures.length} decision(s) failed. Please try again.`);
        return;
      }
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="border border-border rounded-xl bg-card flex flex-col"
      style={{ flex: `0 0 ${COLUMN_WIDTH}px`, minHeight: '400px' }}
    >
      <div className="px-4 py-3 border-b border-border shrink-0 flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground">{formatDayHeading(dateStr)}</p>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
            submitted
              ? 'bg-success/30 border-success/50 text-success'
              : allDayDecided
              ? 'bg-ring/30 border-ring/50 text-ring'
              : 'bg-secondary border-accent text-muted-foreground'
          }`}>
            {submitted ? 'Submitted' : `${decidedInDay}/${exceptionRecs.length}`}
          </span>
          {routineRecs.length > 0 && !submitted && (
            <span className="text-xs px-2 py-0.5 rounded-full border border-border bg-card text-muted-foreground whitespace-nowrap">
              +{routineRecs.length} routine
            </span>
          )}
        </div>
      </div>

      <div className={`flex-1 min-h-0 p-3 flex flex-col gap-2 overflow-y-auto transition-opacity ${submitted ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Exceptions — interactive, unchanged */}
        {groupByJob(exceptionRecs).map((group) => (
          <JobGroup
            key={group[0].recommendation_id}
            group={group}
            mode={mode}
            onDecision={onDecision}
            decisions={decisions}
          />
        ))}

        {/* Routine — collapsed, auto-accepted, amendable */}
        <RoutineSection
          routineRecs={routineRecs}
          mode={mode}
          onDecision={onDecision}
          decisions={decisions}
        />
      </div>

      <div className="px-3 py-3 border-t border-border/70 shrink-0">
        {error && <p className="text-xs text-destructive mb-2">{error}</p>}
        <button
          onClick={handleSubmitDay}
          disabled={!allDayDecided || submitted || submitting}
          className="w-full h-9 rounded-lg bg-white text-background text-xs font-semibold hover:bg-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? 'Submitting...' : submitted ? 'Submitted' : `Submit ${formatDayHeading(dateStr).split(' — ')[0]}`}
        </button>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'planning_decisions_draft';

export function PlanningDecisionPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<ViewMode>('info');

  const grouped = groupByDate(MOCK_RECOMMENDATIONS);

  const [decisions, setDecisions] = useState<Record<string, DecisionPayload>>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(decisions)); }
    catch { /* storage unavailable */ }
  }, [decisions]);

  function handleDecision(payload: DecisionPayload) {
    setDecisions((prev) => ({ ...prev, [payload.recommendation_id]: payload }));
  }

  const exceptionCount = MOCK_RECOMMENDATIONS.filter(isException).length;
  const routineCount = MOCK_RECOMMENDATIONS.length - exceptionCount;
  const exceptionsDecided = MOCK_RECOMMENDATIONS.filter(
    (r) => isException(r) && decisions[r.recommendation_id],
  ).length;

  return (
    <div className="p-6 flex flex-col gap-4 h-full">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-white text-lg font-semibold">Planning Decisions</h1>
          <p className="text-muted-foreground text-xs mt-1">
            {exceptionsDecided}/{exceptionCount} exceptions decided
            <span className="text-muted-foreground"> · {routineCount} routine auto-accepted</span>
          </p>
        </div>
        <ViewModeToggle mode={mode} onChange={setMode} />
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-3 flex-1"
        style={{ scrollbarWidth: 'thin' }}
      >
        {grouped.map(([dateStr, recs]) => (
          <DayColumn
            key={dateStr}
            dateStr={dateStr}
            recs={recs}
            mode={mode}
            decisions={decisions}
            onDecision={handleDecision}
          />
        ))}
      </div>
    </div>
  );
}

// ── Drawer wrapper ────────────────────────────────────────────────────────────

export function PlannerDecisionDrawer({ content }: DrawerProps) {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [isDragging, setIsDragging] = useState(false);

  const maxWidth = typeof window !== 'undefined' ? window.innerWidth * MAX_WIDTH_RATIO : 870;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setWidth(Math.min(window.innerWidth * 0.45, maxWidth));
    }
  }, []);

  function handleDragStart(e: React.MouseEvent) {
    e.preventDefault();
    setIsDragging(true);
    const startX = e.clientX;
    const startWidth = width;

    function handleMouseMove(moveEvent: MouseEvent) {
      const delta = startX - moveEvent.clientX;
      const newWidth = Math.min(Math.max(startWidth + delta, MIN_WIDTH), maxWidth);
      setWidth(newWidth);
    }

    function handleMouseUp() {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }

  return (
    <>
      {/* Collapsed trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed right-0 top-60% -translate-y-1/2 z-40 flex flex-col items-center justify-center gap-2 w-9 py-5 bg-card border border-border border-r-0 rounded-l-xl text-muted-foreground hover:text-white hover:bg-secondary transition-colors"
          title="Open Planning Decisions"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 11L5 7L9 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span
            className="text-xs font-medium tracking-widest uppercase whitespace-nowrap"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            Planner
          </span>
        </button>
      )}

      {/* Drawer — freely resizable, no backdrop */}
      <div
        className="fixed top-24 right-0 h-below-topbar z-50 bg-popover border-l border-border shadow-2xl flex flex-col"
        style={{
          width: open ? `${width}px` : '0px',
          transition: isDragging ? 'none' : 'width 200ms ease-in-out',
          overflow: 'hidden',
        }}
      >
        {/* Drag handle */}
        <div
          onMouseDown={handleDragStart}
          className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-ring/40 active:bg-ring/60 transition-colors z-10"
          style={{ touchAction: 'none' }}
        />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0" style={{ minWidth: MIN_WIDTH }}>
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-ring" />
            <span className="text-sm font-semibold text-white">Planning Decisions</span>
            <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-md bg-card border border-border">
              Margin threshold {MARGIN_THRESHOLD}%
            </span>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-white hover:bg-secondary transition-colors"
            title="Minimise"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-hidden" style={{ minWidth: MIN_WIDTH }}>
          {content}
        </div>
      </div>
    </>
  );
}